import type { Option } from "@hightouch/formkit";
import type { AnalyzedEventSchema } from "src/graphql";

export type { AnalyzedEventSchema };

/**
 * Events UIs use creatable Comboboxes where users can type "expected columns".
 * Many of our Combobox implementations require `value ∈ options` to render the selected label,
 * so we inject the current value into the options list *only if it's missing*.
 *
 * This keeps the Combobox controlled, avoids "blanking" on Enter, and clearly annotates
 * the value as "(expected column)".
 */
export function getExpectedColumnOptions(
  options: readonly Option[],
  value: unknown,
): Option[] {
  const out = [...options];
  const valueString = typeof value === "string" ? value : "";
  if (!valueString) return out;

  const exists = out.some((o) => o.value === valueString);
  if (exists) return out;

  out.push({ label: `${valueString} (expected column)`, value: valueString });
  return out;
}

export type EventFieldSuggestionOption = {
  field: string;
  type: string;
  /**
   * For array fields, the list of fields available on the array element.
   * These are *relative* paths (e.g. `child`, not `properties.items.child`).
   *
   * Important: we intentionally keep this to a single level (no recursive nesting),
   * because the UI only needs one array-element scope at a time.
   */
  elementOptions?: EventFieldSuggestionOption[];
};

/**
 * Given the current subscription filter constraints, select the analyzed schemas
 * that should contribute fields to the suggestion model.
 */
export function selectAnalyzedSchemasForSuggestions(options: {
  analyzedSchemas: AnalyzedEventSchema[];
  derivedEventTypeForSuggestions?: string;
  shouldRestrictToTrackOnly?: boolean;
  typeFromFilter?: string;
}): AnalyzedEventSchema[] {
  const {
    analyzedSchemas,
    derivedEventTypeForSuggestions,
    shouldRestrictToTrackOnly,
    typeFromFilter,
  } = options;

  if (derivedEventTypeForSuggestions) {
    return analyzedSchemas.filter(
      (s) => s.eventType === derivedEventTypeForSuggestions,
    );
  }
  if (shouldRestrictToTrackOnly) {
    return analyzedSchemas.filter(
      (s) =>
        typeof s.eventType === "string" && s.eventType.startsWith("track:"),
    );
  }
  if (typeFromFilter && typeFromFilter !== "track") {
    return analyzedSchemas.filter((s) => s.eventType === typeFromFilter);
  }
  return analyzedSchemas;
}

/**
 * Convert analyzed leaf fields (from event-analysis) into the UI suggestion model used by formkit.
 *
 * INPUT:
 * - Each schema contains leaf fields like `properties.one.two[].child`
 * - `[]` indicates an array segment
 *
 * OUTPUT:
 * - Parent objects are added: `properties`, `properties.one`, ...
 * - Array leaf paths are collapsed to the array field: `properties.one.two`
 * - Array fields get a single-level `elementOptions` list (relative to the array element)
 */
export function buildEventFieldSuggestionOptions(options: {
  analyzedSchemas: AnalyzedEventSchema[];
  derivedEventTypeForSuggestions?: string;
  shouldRestrictToTrackOnly?: boolean;
  typeFromFilter?: string;
}): EventFieldSuggestionOption[] {
  const selectedSchemas = selectAnalyzedSchemasForSuggestions(options);

  // De-dupe leaf fields across schemas by `field`.
  const byField = new Map<string, string>();
  for (const schema of selectedSchemas) {
    for (const f of schema?.fields ?? []) {
      if (!f?.field) continue;
      if (!byField.has(f.field)) byField.set(f.field, f.type);
    }
  }

  /**
   * If the same field path is seen with conflicting types (rare but possible across event types),
   * prefer structural nodes over primitives so the UI remains navigable:
   * `array` > `object` > primitive leaf.
   */
  const rank = (t: string) => (t === "array" ? 3 : t === "object" ? 2 : 1);

  const upsert = (
    map: Map<string, EventFieldSuggestionOption>,
    opt: EventFieldSuggestionOption,
  ) => {
    const existing = map.get(opt.field);
    if (!existing) {
      map.set(opt.field, opt);
      return;
    }
    if (rank(opt.type) > rank(existing.type)) map.set(opt.field, opt);
  };

  const upsertWithObjectParents = (
    map: Map<string, EventFieldSuggestionOption>,
    opt: EventFieldSuggestionOption,
  ) => {
    const parts = opt.field.split(".").filter(Boolean);
    for (let i = 1; i < parts.length; i++) {
      const parent = parts.slice(0, i).join(".");
      if (parent) upsert(map, { field: parent, type: "object" });
    }
    upsert(map, opt);
  };

  const topLevel = new Map<string, EventFieldSuggestionOption>();
  const elementByArrayField = new Map<
    string,
    Map<string, EventFieldSuggestionOption>
  >();

  const addElementField = (
    arrayField: string,
    elementPath: string,
    t: string,
  ) => {
    const byElementField =
      elementByArrayField.get(arrayField) ??
      new Map<string, EventFieldSuggestionOption>();
    elementByArrayField.set(arrayField, byElementField);

    if (!elementPath) return;

    // Only build a single elementOptions level. If we see nested arrays inside the element,
    // we surface the nested array field but do not recurse further.
    const idx = elementPath.indexOf("[]");
    if (idx >= 0) {
      const before = elementPath.slice(0, idx);
      const arrayName = before.endsWith(".") ? before.slice(0, -1) : before;
      if (arrayName)
        upsertWithObjectParents(byElementField, {
          field: arrayName,
          type: "array",
        });
      return;
    }

    upsertWithObjectParents(byElementField, { field: elementPath, type: t });
  };

  for (const [field, type] of byField.entries()) {
    const rawPath = field.trim();
    if (!rawPath) continue;

    if (!rawPath.includes("[]")) {
      upsertWithObjectParents(topLevel, { field: rawPath, type });
      continue;
    }

    // Split on the first array marker. Everything before is the array field.
    const idx = rawPath.indexOf("[]");
    const before = rawPath.slice(0, idx);
    const arrayField = before.endsWith(".") ? before.slice(0, -1) : before;
    if (!arrayField) continue;

    upsertWithObjectParents(topLevel, { field: arrayField, type: "array" });

    // Everything after becomes element options (relative to array element).
    let rest = rawPath.slice(idx + 2);
    if (rest.startsWith(".")) rest = rest.slice(1);
    addElementField(arrayField, rest, type);
  }

  // Attach elementOptions after collecting all element fields (children can be contributed by many leaves).
  for (const [arrayField, elementMap] of elementByArrayField.entries()) {
    const top = topLevel.get(arrayField);
    if (!top || top.type !== "array") continue;
    const elementOptions = Array.from(elementMap.values()).sort((a, b) =>
      a.field.localeCompare(b.field),
    );
    if (elementOptions.length)
      topLevel.set(arrayField, { ...top, elementOptions });
  }

  return Array.from(topLevel.values()).sort((a, b) =>
    a.field.localeCompare(b.field),
  );
}

/**
 * Build dropdown options for the realtime event wizard from a *single* analyzed schema.
 *
 * Rules:
 * - Exclude objects and arrays as selectable leaf fields.
 * - Collapse any array leaf (`a.b[].c`) to the array parent (`a.b`) and include it as an option.
 */
export function buildRealtimeEventFieldOptionsFromSchema(schema: {
  fields: Array<{ field: string; type: string }>;
}): Array<{ label: string; value: string }> | undefined {
  const out = new Set<string>();
  for (const f of schema.fields) {
    if (!f?.field) continue;
    if (f.type === "object") continue;

    if (f.field.includes("[]")) {
      const before = f.field.split("[]")[0] ?? "";
      const collapsed = before.endsWith(".") ? before.slice(0, -1) : before;
      if (collapsed) out.add(collapsed);
      continue;
    }

    if (f.type === "array") continue;
    out.add(f.field);
  }

  const values = Array.from(out).sort((a, b) => a.localeCompare(b));
  return values.length
    ? values.map((v) => ({ label: v, value: v }))
    : undefined;
}
